/**
 * difficulty-model.ts — 魔王難度 × 場內升級 的可執行模型(純函式)。
 *
 * 緣由(boss 2026-06-14):「魔王要有挑戰性。前面的小兵都是為了該場次的升級,要升級
 * 夠強才能打贏魔王。魔王沒有一定難度,前面就會很無聊。」完整推導見
 * docs/spec/DIFFICULTY_PROGRESSION_MODEL.md。
 *
 * 這裡把「玩家 DPS 估計 → 魔王 TTK」寫成純函式,讓難度校準可單測、不再瞎調。
 * 鐵則:純計算,不讀 progress / 不寫修煉值;只描述「在預設連發砲、mastery=1」基準下的
 * 戰力,武器切換(railgun/mortar…)是 sidegrade、不在此模型內。
 *
 * ── 第二輪擴充(boss 2026-06-14「王都被秒殺、後面不用動就贏」)──────────────────
 *   舊模型自承「只算預設連發砲 + 中庸 4 抽 + mastery=1 ≈ 84 DPS,排除武器切換/技能槽/
 *   後期堆疊」。但 railgun 等武器 + maxed build 的真實 DPS 遠超 84 → 王照 84 校 → 被秒。
 *   下方補上 §7 的純函式:把「武器倍率 + 升級乘法堆疊(封頂)+ 技能/mastery 貢獻」算進來,
 *   產 weak/median/strong 三檔真實單體 DPS,讓校準有依據可單測。仍是純計算、不碰學習軌。
 */

import { RUN_DAMAGE_MULT_CAP, CRIT_AVG_MULT_CAP, AUTO_FIRE_MULT_FLOOR } from '../systems/run-modifiers';
import { weaponLevelDamageMult } from '../weapons/types';

/** 每秒輸出(單體、每發命中)。bulletDamage × 每秒發數。 */
export function dpsFrom(bulletDamage: number, fireIntervalMs: number): number {
  if (!(bulletDamage > 0) || !(fireIntervalMs > 0)) return 0;
  return bulletDamage * (1000 / fireIntervalMs);
}

/** 全新 0 抽基準 DPS(預設武器、無三選一加成、mastery=1)。 */
export function baselineDps(bulletDamage: number, fireIntervalMs: number): number {
  return dpsFrom(bulletDamage, fireIntervalMs);
}

/**
 * 「中庸 4 抽」估計 DPS:代表一場標準關卡(~4 次三選一)抽到的常見強化組合 —
 * +10 傷(重砲)、暴擊 35%×2(平均 ×1.35)、連射 ×0.8(射速)。見 spec §2。
 * 這是**校準魔王 HP 的基準對手**(不是最強也不是最弱,是中位數)。
 */
export function median4PickDps(baseBulletDamage: number, baseFireIntervalMs: number): number {
  const damage = baseBulletDamage + 10;          // 重砲 +10
  const critAvg = 1.35;                            // 暴擊 35% × 2 倍 → 平均 1.35
  const fireInterval = baseFireIntervalMs * 0.8;   // 連射 ×0.8(越小越快)
  return dpsFrom(damage, fireInterval) * critAvg;
}

/** 純輸出秒殺時間(秒)。實戰更久(要走位閃招),這是下限參考。 */
export function ttkSeconds(bossHp: number, dps: number): number {
  if (!(dps > 0)) return Infinity;
  return bossHp / dps;
}

/**
 * tier1(第一章)魔王的「中庸 4 抽 TTK」設計窗(秒)。
 * 下限:撐得過二階段變身(50% HP)+ ≥1 次衝擊波(warn 1.2s)+ 瞬移演出。
 * 上限:K-3 小孩不耐(別變海綿)。spec §4。
 */
export const TIER1_TTK_WINDOW_S: readonly [number, number] = [8, 13];

/** 反推:要讓中庸 4 抽 TTK 落在目標秒數,魔王該有多少 HP。 */
export function recommendedBossHp(targetTtkS: number, baseBulletDamage: number, baseFireIntervalMs: number): number {
  return Math.round(median4PickDps(baseBulletDamage, baseFireIntervalMs) * targetTtkS);
}

// ── 波內逐波強化 ──────────────────────────────────────────────────────────────
// boss 2026-06-17:「每一波的小兵應該要變強」。原本同關 5 波小兵 HP 相同(縮放只綁
// 章/關不綁 wave,後段波只是「數量多」不「更強」,見 COMBAT_BALANCE_OVERVIEW §4b)。
// 這裡讓同關內 wave 越後 HP 越硬:線性 step + 封頂(避免末波變海綿、守可躲/不拖鐵則)。
// 與章難度 enemyHpMult 相乘(後段章節的後段波最硬)。
export const WAVE_HP_STEP = 0.18;     // 每後一波 +18% HP
export const WAVE_HP_MULT_CAP = 2.0;  // 封頂 ×2

/** 同關內第 wave 波(0-indexed)的小兵 HP 倍率。wave0=1.0,線性 +step,夾在 [1, cap]。 */
export function waveStrengthHpMult(wave: number, step = WAVE_HP_STEP, cap = WAVE_HP_MULT_CAP): number {
  const w = Math.max(0, Math.floor(wave));
  return Math.min(cap, 1 + w * step);
}

// ── 成長契約:打完一關拿幾次三選一 ────────────────────────────────────────────
// 「前面的小兵都是為了該場次的升級」→ 一場該產出固定範圍的升級次數(picks)。
// 守住這個數 = 守住「殺兵有意義 / 升級夠強才打得贏」。RunXp 每場重置,所以這算的是
// 「單關內」可拿的 picks。picks = 抵達等級 - 1(L1 起跳,每升一級一抽)。

/**
 * 一關清完可得的單場 XP 總量。
 *   雜兵:Σ波次量 × XP_PER_KILL × 平均 variant 倍率(basic1/fast1.5/tank2/elite3)。
 *   小王:小王數 × XP_PER_KILL × 小王倍率。
 * avgVariantMult 是該關敵種組成的加權平均(ch1 偏 basic/fast ≈ 1.25)。
 */
export function runXpForClear(
  waveSizes: readonly number[],
  avgVariantMult: number,
  miniBossCount: number,
  miniBossMult: number,
  xpPerKill: number,
): number {
  const mobs = waveSizes.reduce((a, b) => a + b, 0);
  return mobs * xpPerKill * avgVariantMult + miniBossCount * xpPerKill * miniBossMult;
}

/**
 * 給定單場 XP 總量,算抵達的等級(= RunXp.add 的逐門檻消耗,picks = 等級-1)。
 * thresholdFn(level) 回傳「從 level 升到 level+1 需要的 XP」(= xpThresholdForLevel)。
 * 純函式、與 RunXp.add 的累積邏輯等價(逐級扣門檻直到剩餘 < 下一級門檻)。
 */
export function levelsFromTotalXp(totalXp: number, thresholdFn: (level: number) => number): number {
  if (!(totalXp > 0)) return 1;
  let level = 1;
  let acc = 0;
  // 上限保險:避免 thresholdFn 退化成 0/負時無限迴圈。
  for (let guard = 0; guard < 1000; guard++) {
    const need = thresholdFn(level);
    if (!(need > 0) || acc + need > totalXp) break;
    acc += need;
    level += 1;
  }
  return level;
}

// ── §7 真實武器 DPS + 升級堆疊(封頂)+ 技能/mastery 貢獻 ──────────────────────────
// (boss 2026-06-14 第二輪)補掉舊模型「sidegrade 不算 / 後期堆疊不算」盲點。
// 全部純函式;用真實常數(weaponLevelDamageMult / RUN_DAMAGE_MULT_CAP / CRIT_AVG_MULT_CAP /
// AUTO_FIRE_MULT_FLOOR)算各武器在 weak/median/strong 三檔的單體 DPS。

/** 一把武器的計算用設定(從各 behavior 的 *_DAMAGE_MULT + 發射間隔抓真實常數)。 */
export interface WeaponDpsSpec {
  /** 單發/單 tick 傷害倍率(base bullet damage 的倍數,= 各武器 *_DAMAGE_MULT)。 */
  damageMult: number;
  /** 發射/施傷間隔(ms;railgun=蓄力ms、laser/spin=tick間隔、其餘=發射間隔)。 */
  intervalMs: number;
  /** 命中持續率(0..1):laser 過熱 uptime 折扣,其餘武器=1(永遠在輸出)。 */
  uptime?: number;
}

/** 一個 build 檔位:升級牌帶來的傷害/暴擊/射速 + 學習 mastery + 武器等級。 */
export interface BuildProfile {
  /** 「重砲」類 flat 傷加總(加到 base bullet damage 上)。 */
  flatDamageBonus: number;
  /** 連射卡乘積(各張 autoFireMult 相乘;1 = 無連射)。實際生效會 clamp 下限。 */
  autoFireProduct: number;
  /** 暴擊機率(0..1)。 */
  critChance: number;
  /** 暴擊倍率(2 = 2×,3 = 超新星)。 */
  critMult: number;
  /** 學習軌 mastery 傷害倍率(1 = 沒念書;合法無上限,不受 cap)。 */
  masteryMult: number;
  /** 該武器等級(weaponLevelDamageMult 曲線;1 = 初始)。 */
  weaponLevel: number;
}

/**
 * 升級牌「單發傷害倍率(相對 base)」封頂後的值(boss 2026-06-14 第二輪)。
 * = clamp((base + flat)/base × weaponLevelMult, 1, RUN_DAMAGE_MULT_CAP)。**不含 mastery**
 * (mastery 學習軌合法無上限,由呼叫端在 cap 外另乘)。與 scene `_effectiveBulletDamage`
 * + `RunModifiers.cappedDamageMult` 的封頂語意一致,讓模型 = 實戰。
 */
export function cappedUpgradeDamageMult(baseBulletDamage: number, flatDamageBonus: number, weaponLevel: number): number {
  if (!(baseBulletDamage > 0)) return 1;
  const overBase = (baseBulletDamage + Math.max(0, flatDamageBonus)) / baseBulletDamage
    * weaponLevelDamageMult(weaponLevel);
  return Math.min(RUN_DAMAGE_MULT_CAP, Math.max(1, overBase));
}

/**
 * 平均暴擊倍率封頂後的值(boss 2026-06-14 第二輪)。= clamp(1 + min(1,chance)×(mult-1), 1,
 * CRIT_AVG_MULT_CAP)。與 `RunModifiers.cappedCritAvg` 一致。
 */
export function cappedCritAvgMult(critChance: number, critMult: number): number {
  const c = Math.max(0, Math.min(1, Number.isFinite(critChance) ? critChance : 0));
  const m = Number.isFinite(critMult) && critMult > 1 ? critMult : 1;
  return Math.min(CRIT_AVG_MULT_CAP, Math.max(1, 1 + c * (m - 1)));
}

/** 連射卡乘積套下限 clamp 後的有效射速倍率(= RunModifiers.effectiveAutoFireMult 語意)。 */
export function effectiveAutoFire(autoFireProduct: number): number {
  const v = Number.isFinite(autoFireProduct) && autoFireProduct > 0 ? autoFireProduct : 1;
  return Math.max(AUTO_FIRE_MULT_FLOOR, v);
}

/**
 * 一把武器在某 build 檔位下的「真實單體 DPS」。
 *
 *   perHit = round(base × cappedUpgradeMult × masteryMult × weaponDamageMult)
 *   shotsPerSec = 1000 / (intervalMs × effectiveAutoFire)
 *   DPS = perHit × shotsPerSec × cappedCritAvg × uptime
 *
 * 與 scene 實戰路徑(_effectiveBulletDamage 封頂 + applyRateMod 縮間隔 + 過熱 uptime)
 * 對齊。純函式,用真實常數,不讀 progress / 不寫修煉值。
 */
export function weaponDpsForBuild(weapon: WeaponDpsSpec, baseBulletDamage: number, profile: BuildProfile): number {
  if (!(baseBulletDamage > 0) || !(weapon.intervalMs > 0)) return 0;
  const upgradeMult = cappedUpgradeDamageMult(baseBulletDamage, profile.flatDamageBonus, profile.weaponLevel);
  const masteryMult = Number.isFinite(profile.masteryMult) && profile.masteryMult > 0 ? profile.masteryMult : 1;
  const perHit = Math.max(1, Math.round(baseBulletDamage * upgradeMult * masteryMult * weapon.damageMult));
  const interval = weapon.intervalMs * effectiveAutoFire(profile.autoFireProduct);
  const shotsPerSec = 1000 / interval;
  const critAvg = cappedCritAvgMult(profile.critChance, profile.critMult);
  const uptime = Number.isFinite(weapon.uptime) && (weapon.uptime as number) > 0 ? (weapon.uptime as number) : 1;
  return perHit * shotsPerSec * critAvg * uptime;
}

/** strong build 的 TTK 地板(秒):強 build 也得撐這麼久,王才演得出招、玩家得躲。tier1 基準。 */
export const STRONG_TTK_FLOOR_TIER1_S = 4;

// ── §8 永久 meta 天花板納入「玩家最大真實 DPS」估計 ──────────────────────────────
// (boss 2026-06-15「玩遊戲可永久變強」,spec LONG_TERM_PROGRESSION.md §3)
//
// 永久 meta(PowerUp 商店)讓玩家 base 傷害/HP 永久微抬(全滿 ≤ +25% 傷害,
// 見 meta-shop.META_DAMAGE_CAP)。難度模型若不納入,高檔內容(難度旗幟/無盡塔/後章)
// 會被「meta 全滿的玩家」秒掉 → 同 player.json/王 HP 校準教訓重演。
//
// 解法:把 meta 傷害天花板當「玩家最大真實 DPS」的乘數,讓高檔內容校在
// 「中庸單場 build × META 全滿」之上(§3「挑戰牆永遠在 meta 天花板之上」)。
// 主線維持 0-meta 可過(不乘此係數);只有「可選加難」內容乘 metaCeilingMult。
// 純計算、不讀 progress / 不寫修煉值(與本檔其餘函式同款)。

/**
 * 把永久 meta 傷害天花板折成「玩家最大真實 DPS」乘數。
 * = clamp(metaDamageCap, 1, …)。預設取 meta-shop 的 META_DAMAGE_CAP(全滿 = 1.25)。
 * caller(高檔內容 HP 校準)用:playerMaxDps = weaponDpsForBuild(strong) × metaCeilingMult。
 * 學習層接縫(§6):若日後要把 learningBonus 也納入,再乘 (1 + learningBonus),預設 0。
 */
export function metaCeilingMult(metaDamageCap: number, learningBonus: number = 0): number {
  const cap = Number.isFinite(metaDamageCap) && metaDamageCap > 1 ? metaDamageCap : 1;
  const lb = Number.isFinite(learningBonus) && learningBonus > 0 ? learningBonus : 0;
  return cap * (1 + lb);
}

/**
 * 高檔(可選加難)內容的建議王 HP:把 meta 天花板納入「玩家最大真實 DPS」後反推。
 * = round(strongBuildDps × metaCeilingMult(metaDamageCap) × targetTtkS)。
 * 確保不管 meta 多滿,高檔內容 TTK 不被 meta 拉垮(§3)。
 * 主線**不要**用此函式(主線校在 0-meta median 上,維持「0 meta 也可過」)。
 */
export function recommendedHighTierBossHp(
  strongBuildDps: number,
  metaDamageCap: number,
  targetTtkS: number,
): number {
  const dps = strongBuildDps > 0 ? strongBuildDps : 0;
  const ttk = targetTtkS > 0 ? targetTtkS : 0;
  return Math.round(dps * metaCeilingMult(metaDamageCap) * ttk);
}
